/**
 * @file profileService.js
 * @description Therapist and agency profile data access layer. Handles loading,
 *   saving, geocoding, credential verification lifecycle, and agency onboarding.
 *   Provides DB↔UI field mapping via dbToTherapist / dbToAgency transforms.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/profileService
 */
import { supabase } from "./supabase";
import { getCachedConfig } from "./configService";
import { createNotification } from "./notificationService";
import { invokeNotice } from "./moderationNotice";
import { checkDuplicateSignals } from "./fraudService";
import { normalizeCredentialedMonth } from "./experience";
import { normalizeAccreditations } from "./accreditations";
import { TYPE_COLOR, roleTypeKey, typeLabel } from "../theme";

/**
 * @description Computes years of experience from the year a therapist was
 *   credentialed. Returns 0 for missing/invalid years, and clamps negatives
 *   (a future year) to 0. Single source of truth for the credential-year →
 *   experience derivation used across mappers.
 * @param {number|string} year - 4-digit year credentialed (e.g. 2019)
 * @returns {number} Years of experience (>= 0)
 */
export function yearsFromCredentialYear(year) {
  const y = parseInt(year);
  if (!y) return 0;
  return Math.max(0, new Date().getFullYear() - y);
}

/**
 * @description Geocodes a US ZIP code to lat/lng using Nominatim (free, no API key).
 *   Do not call in tight loops — Nominatim has rate limits.
 * @param {string} zip - 5-digit US ZIP code
 * @returns {Promise<{lat: number, lng: number}|null>} Coordinates or null on failure
 */
export async function geocodeZip(zip) {
  if (!zip || !zip.trim()) return null;
  try {
    const q = encodeURIComponent(zip.trim());
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=us`,
      { headers: { "Accept-Language": "en" } }
    );
    const data = await res.json();
    if (data?.[0]) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
  } catch (_) {}
  return null;
}

/**
 * @description Loads a therapist's full profile by joining therapist_profiles
 *   and profiles tables. Returns the merged UI-shaped object.
 * @param {string} userId - The therapist's Supabase user ID
 * @returns {Promise<Object|null>} Merged therapist profile or null if not found
 */
export async function loadTherapistProfile(userId) {
  const [{ data: therapistRow, error: therapistError }, { data: profileRow, error: profileError }] = await Promise.all([
    supabase
      .from("therapist_profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("profiles")
      .select("full_name, email, phone, avatar_url, role")
      .eq("id", userId)
      .maybeSingle(),
  ]);

  if (therapistError) throw therapistError;
  if (profileError) throw profileError;
  if (!therapistRow && !profileRow) return null;

  return dbToTherapist(therapistRow || {}, profileRow || {});
}

/**
 * @description Saves (upserts) a therapist profile to therapist_profiles and updates
 *   the profiles table. Geocodes the ZIP if lat/lng are missing. Validates required fields.
 * @param {string} userId - The therapist's Supabase user ID
 * @param {Object} profile - The UI-shaped therapist profile object
 * @returns {Promise<void>}
 */
export async function saveTherapistProfile(userId, profile, opts = {}) {
  // Enforce mandatory fields — prevent saving incomplete profiles. The minimal
  // "Finish later" onboarding path (name + ZIP only) passes allowIncomplete so a
  // therapist can exist as a map pin and enrich the rest later; the ZIP is still
  // geocoded below, and isProfileComplete() keeps them flagged "in progress".
  if (!opts.allowIncomplete) {
    const missing = [];
    if (!(profile.skills || []).length)       missing.push("skills");
    if (!(profile.availability || []).length) missing.push("availability");
    if (!(profile.settings || []).length)     missing.push("work settings");
    if (missing.length > 0) throw new Error(`Profile incomplete, missing: ${missing.join(", ")}`);
  }

  const row = therapistToDb(userId, profile);

  // Geocode the ZIP to persist lat/lng so market reach can use it without
  // hitting Nominatim on every reach computation.
  if (profile.zip && (!profile.lat || !profile.lng)) {
    try {
      const coords = await geocodeZip(profile.zip);
      if (coords) { row.lat = coords.lat; row.lng = coords.lng; }
    } catch (_) {}
  } else if (profile.lat && profile.lng) {
    row.lat = profile.lat;
    row.lng = profile.lng;
  }

  const { error } = await supabase
    .from("therapist_profiles")
    .upsert(row, { onConflict: "user_id" });

  if (error) throw error;

  const profilePatch = {
    full_name: profile.name || null,
    phone: profile.phone || null,
  };
  if (profile.photoUrl) {
    profilePatch.avatar_url = profile.photoUrl;
  }

  const { error: profileError } = await supabase
    .from("profiles")
    .update(profilePatch)
    .eq("id", userId);

  if (profileError) throw profileError;
}

/**
 * Maps a raw Supabase therapist_profiles row + profiles row into the UI
 * therapist object shape. The clinical-details key is returned under the
 * credential-appropriate name so the UI never has to guess:
 *   - "bcba"  → profile.bcbaDetails
 *   - "bcaba" → profile.bcabaDetails
 *   - others  → profile.bcbaDetails (safe fallback, field will be empty)
 */
function dbToTherapist(row, profileRow = {}) {
  const settingsJson   = row.settings_json || {};
  const clinicalData   = row.bcaba_details || settingsJson.bcbaDetails || {};
  const credential     = row.credential || "";

  return {
    credential,
    // Role drives fit weighting + which clinical section the editor reads. It
    // lives on profiles.role, NOT in `credential` (a BACB number) — using the
    // credential string here was the root cause of BCBA/BCaBA mis-scoring.
    role:            profileRow.role || "",
    verified:            row.verified            || false,
    verificationStatus:  row.verification_status || "unverified",
    verificationRejectionReason: row.verification_rejection_reason || "",
    expirationDate:  row.expiration_date  || "",
    city:            row.city           || "",
    state:           row.state          || "",
    zip:             row.zip            || "",
    lat:             row.lat            ?? null,
    lng:             row.lng            ?? null,
    // Years of experience is DERIVED from the credentialed year so it never goes
    // stale. credentialedYear is the editable source of truth; `experience` is a
    // computed read-only value (falls back to the legacy snapshot column when no
    // credentialed year is set on the row yet).
    credentialedYear: row.credentialed_year ?? null,
    // Month-precision credential date ("YYYY-MM") — drives friendly month labels.
    credentialedDate: row.credentialed_date ? String(row.credentialed_date).slice(0, 7) : null,
    experience:      row.credentialed_year ? String(yearsFromCredentialYear(row.credentialed_year)) : String(row.experience ?? 0),
    skills:          row.skills         || [],
    ageGroups:       row.age_groups     || [],
    settings:        row.settings       || [],
    employment:      row.employment     || [],
    minHours:        String(row.min_hours  ?? ""),
    maxHours:        String(row.max_hours  ?? ""),
    maxDistance:     String(row.max_distance ?? 20),
    openToRemote:    row.open_to_remote    || false,
    openToLicensure: row.open_to_licensure || false,
    licensedStates:  row.licensed_states   || [],
    // "Open to relocating" — a willingness to move for an in-person case, separate from
    // maxDistance (daily travel). Read by the case view's reachability + hire chance.
    openToRelocation: row.open_to_relocation || false,
    // NPPES licensure corroboration (read-only here — server-stamped + RLS-pinned).
    npi:                row.npi                  || "",
    nppesVerified:      row.nppes_verified       || false,
    nppesVerifiedAt:    row.nppes_verified_at    || null,
    nppesLicenses:      row.nppes_licenses       || [],
    nppesLicenseStates: row.nppes_license_states || [],
    availability:        row.availability   || [],
    availabilityPeriods: settingsJson.availabilityPeriods || [],
    transportation:      row.transportation || settingsJson.transportation || "",
    workExperience:      settingsJson.workExperience || "",
    name:            profileRow.full_name || "",
    full_name:       profileRow.full_name || "",
    email:           profileRow.email || "",
    phone:           profileRow.phone || "",
    photoUrl:        row.photo_url || profileRow.avatar_url || "",
    resumeUrl:       row.resume_url     || "",
    resumePath:      row.resume_path    || "",
    summary:         row.bio            || "",
    bio:             row.bio            || "",
    coverLetter:     row.cover_letter   || "",
    sex:             row.sex            || "",
    languages:       row.languages      || [],
    certifications:  row.certifications || [],
    payerEnrollments: row.payer_enrollments || [],
    bgCheck:             settingsJson.backgroundCheck || clinicalData.backgroundCheck || "",
    hasLLC:              settingsJson.hasLLC || false,
    referencesAvailable: settingsJson.referencesAvailable || false,
    behaviorComfort: row.behavior_comfort || [],
    // Clinical details live in ONE DB column (bcaba_details). Expose them under
    // BOTH keys so each role's editor finds them — the BCBA UI reads bcbaDetails,
    // the BCaBA UI reads bcabaDetails. (Previously keyed off the credential
    // string, which is a BACB number, never the literal "bcaba" → isBCaBA was
    // always false → BCaBA clinical fields rendered blank after every reload.)
    bcbaDetails:     clinicalData,
    bcabaDetails:    clinicalData,
  };
}

function therapistToDb(userId, p) {
  const bcbaDetails = p.bcbaDetails && Object.keys(p.bcbaDetails).length > 0
    ? p.bcbaDetails
    : p.bcabaDetails && Object.keys(p.bcabaDetails).length > 0
      ? p.bcabaDetails
      : {};

  const settingsJson = {};
  if (Object.keys(bcbaDetails).length > 0) {
    settingsJson.bcbaDetails = bcbaDetails;
  }
  if (p.bgCheck) {
    settingsJson.backgroundCheck = p.bgCheck;
  }
  if (p.hasLLC) {
    settingsJson.hasLLC = true;
  }
  if (p.referencesAvailable) {
    settingsJson.referencesAvailable = true;
  }
  if (p.availabilityPeriods?.length) {
    settingsJson.availabilityPeriods = p.availabilityPeriods;
  }
  if (p.workExperience) {
    settingsJson.workExperience = p.workExperience;
  }

  const credMonth = normalizeCredentialedMonth(p.credentialedDate);

  return {
    user_id:          userId,
    credential:       p.credential       || null,
    expiration_date:  p.expirationDate   || null,
    city:             p.city             || null,
    state:            p.state            || null,
    zip:              p.zip              || null,
    // Persist month-precision credentialed_date ("YYYY-MM-01") as the new source
    // of truth, AND keep credentialed_year + the legacy `experience` snapshot
    // column correct for backward compatibility with the ~15 consumers that still
    // read them. credentialed_year is derived from the date when one is captured.
    //
    // credMonth is normalized (not trusted raw): credentialed_date is a real
    // Postgres `date`, and <input type="month"> falls back to a plain text box
    // in Safari, so unvalidated input used to reach here and make the whole
    // upsert throw — silently stranding the user mid-onboarding. Unusable
    // values now degrade to null instead of failing the entire profile save.
    credentialed_date: credMonth ? `${credMonth}-01` : null,
    credentialed_year: credMonth
      ? parseInt(credMonth.slice(0, 4))
      : (parseInt(p.credentialedYear) || null),
    experience:       credMonth
      ? yearsFromCredentialYear(parseInt(credMonth.slice(0, 4)))
      : (p.credentialedYear ? yearsFromCredentialYear(p.credentialedYear) : (parseInt(p.experience) || 0)),
    skills:           p.skills           || [],
    age_groups:       p.ageGroups        || [],
    settings:         p.settings         || [],
    employment:       p.employment       || [],
    min_hours:        parseInt(p.minHours)  || null,
    max_hours:        parseInt(p.maxHours)  || null,
    max_distance:     parseInt(p.maxDistance) || 20,
    // Remote election is driven by the "Remote" work-setting chip — single source
    // of truth (no separate toggle). RBTs never have Remote in settings, so this
    // is naturally false for them. open_to_licensure + licensed_states only carry
    // meaning when remote is on.
    open_to_remote:    (p.settings || []).includes("Remote"),
    open_to_licensure: p.openToLicensure || false,
    licensed_states:   p.licensedStates  || [],
    // Only written when the caller carries the field, so a save path that builds a
    // partial profile cannot silently reset someone's election to false.
    ...(p.openToRelocation === undefined ? {} : { open_to_relocation: !!p.openToRelocation }),
    availability:     p.availability     || [],
    photo_url:        p.photoUrl         || null,
    bio:              p.summary || p.bio || null,
    cover_letter:     p.coverLetter      || null,
    sex:              p.sex              || null,
    languages:        p.languages        || [],
    certifications:   p.certifications   || [],
    payer_enrollments: p.payerEnrollments || [],
    behavior_comfort: p.behaviorComfort  || [],
    settings_json:    settingsJson,
    transportation:   p.transportation   || null,
    resume_url:       p.resumeUrl        || null,
    bcaba_details:    Object.keys(bcbaDetails).length > 0 ? bcbaDetails : null,
    // verification_status is intentionally NOT written from the client. A stale
    // client (e.g. a therapist saving their profile minutes after an admin
    // verified them) would otherwise clobber the admin's "verified" back to
    // "unverified". The re-verify-on-cred-change rule is enforced server-side by
    // the reset_verification_on_cred_change trigger, which resets to "unverified"
    // only when the credential number or expiration date actually changes and
    // never for admin/service writes. See migration 20260709140000.
  };
}

// ── Verification flow ────────────────────────────────────────────────────────

// Therapist submits for verification
export async function requestVerification(userId, credentialNumber) {
  if (!userId) throw new Error("You must be signed in to submit for verification.");

  // Use upsert so new users who haven't saved a full profile yet can still
  // submit for verification. If the row doesn't exist it will be created with
  // just the credential + status; all other columns default to null / [].
  const { error } = await supabase
    .from("therapist_profiles")
    .upsert(
      { user_id: userId, verification_status: "pending", credential: credentialNumber },
      { onConflict: "user_id", ignoreDuplicates: false }
    );
  if (error) throw error;

  // Log to verification_requests if the table exists
  await supabase.from("verification_requests").insert({
    user_id: userId,
    credential_number: credentialNumber,
    status: "pending",
    submitted_at: new Date().toISOString(),
  }).then(() => {}).catch(() => {});

  // Notify all admin users in-app
  try {
    const [{ data: admins }, { data: therapistProfile }] = await Promise.all([
      supabase.from("profiles").select("id").eq("role", "admin"),
      supabase.from("profiles").select("full_name").eq("id", userId).maybeSingle(),
    ]);
    const name = therapistProfile?.full_name || "A provider";
    if (admins?.length) {
      await Promise.all(
        admins.map(a => createNotification({
          user_id: a.id,
          type: "verification_request",
          title: "New verification request",
          body: `${name} submitted credential ${credentialNumber} for review.`,
          read: false,
        }))
      );
    }
  } catch (_) {}

  // Email the platform admin out-of-band so they're notified even when not in
  // the app. Fire-and-forget — a failure here must never fail the submission.
  // The edge function reads the admin address + toggle from platform_config and
  // identifies the therapist from their JWT (auto-attached by supabase-js).
  supabase.functions
    .invoke("notify-admin-verification", { body: { credential: credentialNumber } })
    .catch(() => {});
}

// Admin approves a verification request
// role: "rbt" | "bcba" — used to tailor the renewal reminder in the notification
export async function approveVerification(userId, role, note) {
  const { error: tpErr } = await supabase.from("therapist_profiles")
    .update({ verification_status: "verified", ...(note ? { verification_notes: note } : {}) })
    .eq("user_id", userId);
  if (tpErr) throw tpErr;
  await supabase.from("verification_requests")
    .update({ status: "approved", reviewed_at: new Date().toISOString() })
    .eq("user_id", userId).eq("status", "pending")
    .then(() => {}).catch(() => {});

  const cycleNote = (role === "bcba" || role === "bcaba")
    ? " Your certification renews every 2 years. You'll be reminded 60 days before expiry."
    : " Your certification renews annually. You'll be reminded 30 days before expiry.";

  const credLabel = typeLabel(roleTypeKey(role) || "RBT");
  // Non-fatal — DB status already updated; notification requires admin INSERT policy on notifications table
  await createNotification({
    user_id: userId,
    type: "verification_approved",
    title: "Credential verified",
    body: `Your BACB ${credLabel} credential has been verified. A trust badge is now shown on your profile and all applications.${cycleNote}`,
    read: false,
  }, { skipDedupe: true }).catch(err => {
    console.warn("[approveVerification] notification insert blocked by RLS — apply admin_insert_notifications policy:", err?.message);
  });

  // Email the therapist the outcome too (besides the in-app alert). Admin-gated edge function. The approval
  // already stands, so a notice that did not go is returned as text, never thrown.
  return { noticeProblem: await invokeNotice("notify-verification-result", { user_id: userId, outcome: "approved", role }) };
}

// Admin rejects a verification request
export async function rejectVerification(userId, reason) {
  const { error: tpErr } = await supabase.from("therapist_profiles")
    .update({ verification_status: "rejected", verification_rejection_reason: reason })
    .eq("user_id", userId);
  if (tpErr) throw tpErr;
  await supabase.from("verification_requests")
    .update({ status: "rejected", rejection_reason: reason, reviewed_at: new Date().toISOString() })
    .eq("user_id", userId).eq("status", "pending")
    .then(() => {}).catch(() => {});

  // Non-fatal — DB status already updated; notification requires admin INSERT policy on notifications table.
  // Apply this SQL in Supabase Dashboard → SQL Editor to enable notifications:
  //   DROP POLICY IF EXISTS "admin_insert_notifications" ON notifications;
  //   CREATE POLICY "admin_insert_notifications" ON notifications FOR INSERT
  //   WITH CHECK (EXISTS (SELECT 1 FROM profiles WHERE profiles.id = auth.uid() AND profiles.role = 'admin'));
  await createNotification({
    user_id: userId,
    type: "verification_rejected",
    title: "Verification not approved",
    body: reason
      ? `Your credential verification was not approved: ${reason}. Please review and resubmit.`
      : "Your credential verification was not approved. Please check your credential number and resubmit.",
    read: false,
  }, { skipDedupe: true }).catch(err => {
    console.warn("[rejectVerification] notification insert blocked by RLS — apply admin_insert_notifications policy:", err?.message);
  });

  // Email the therapist the outcome too. A notice that did not go is returned as text, never thrown.
  return { noticeProblem: await invokeNotice("notify-verification-result", { user_id: userId, outcome: "rejected", reason: reason || "" }) };
}

// Admin flags a verification for clarification — a "second chance" state.
// Softer than reject: used when the therapist is a likely-real provider whose
// submitted credential number or expiration date didn't match the BACB
// registry (typo, wrong year segment, wrong date, name found under a different
// number). Moves to "under_review", stores the therapist-facing message, and
// notifies them (in-app + email) to correct and resubmit. Resubmission uses the
// existing requestVerification path (→ "pending") — the process is unchanged.
export async function requestVerificationReview(userId, message) {
  const msg = (message || "").trim();

  // Append a short, distinct internal note ("Requested review on <date>") to
  // verification_notes so the admin who reopens this record later sees SOME
  // trail even before anyone clicks Approve — separate from the therapist-
  // facing message (verification_rejection_reason, below) and the admin
  // audit log entry the caller adds. Read-modify-write since this is a
  // running log, not a single overwritable field.
  const { data: existing } = await supabase.from("therapist_profiles")
    .select("verification_notes")
    .eq("user_id", userId)
    .maybeSingle();
  const reviewLogEntry = `Requested review on ${new Date().toISOString().slice(0, 10)}`;
  const nextNotes = existing?.verification_notes
    ? `${existing.verification_notes}\n${reviewLogEntry}`
    : reviewLogEntry;

  const { error: tpErr } = await supabase.from("therapist_profiles")
    .update({ verification_status: "under_review", verification_rejection_reason: msg || null, verification_notes: nextNotes })
    .eq("user_id", userId);
  if (tpErr) throw tpErr;
  await supabase.from("verification_requests")
    .update({ status: "under_review", rejection_reason: msg || null, reviewed_at: new Date().toISOString() })
    .eq("user_id", userId).eq("status", "pending")
    .then(() => {}).catch(() => {});

  // In-app notification (non-fatal — requires admin INSERT policy on notifications).
  await createNotification({
    user_id: userId,
    type: "verification_under_review",
    title: "Action needed: confirm your credential",
    body: msg
      ? `We couldn't verify your credential yet: ${msg} Please correct it and resubmit.`
      : "We couldn't verify your credential yet. Please double-check your credential number and expiration date, then resubmit.",
    read: false,
  }, { skipDedupe: true }).catch(err => {
    console.warn("[requestVerificationReview] notification insert blocked by RLS — apply admin_insert_notifications policy:", err?.message);
  });

  // Email the therapist too. A notice that did not go is returned as text, never thrown.
  return { noticeProblem: await invokeNotice("notify-verification-result", { user_id: userId, outcome: "under_review", reason: msg }) };
}

// Admin re-nudges a therapist who is stuck (rejected / under_review / expired)
// to correct their credential and resubmit. Unlike requestVerificationReview,
// this does NOT change verification_status — it just re-sends the existing ask
// (their current rejection reason) via in-app notification + email, and records
// that a reminder went out so the admin can see they already nudged.
export async function sendVerificationReminder(userId) {
  if (!userId) throw new Error("Missing user id.");

  const { data: existing } = await supabase.from("therapist_profiles")
    .select("verification_status, verification_rejection_reason, verification_notes")
    .eq("user_id", userId)
    .maybeSingle();

  const status = existing?.verification_status || "unverified";
  const reason = (existing?.verification_rejection_reason || "").trim();

  // Append a dated trail so the admin can see a reminder already went out.
  const logEntry  = `Reminder sent on ${new Date().toISOString().slice(0, 10)}`;
  const nextNotes = existing?.verification_notes
    ? `${existing.verification_notes}\n${logEntry}`
    : logEntry;
  await supabase.from("therapist_profiles")
    .update({ verification_notes: nextNotes })
    .eq("user_id", userId)
    .then(() => {}).catch(() => {});

  // In-app notification (non-fatal — requires admin INSERT policy on notifications).
  await createNotification({
    user_id: userId,
    type: "verification_reminder",
    title: "Reminder: confirm your credential",
    body: reason
      ? `Just a reminder: ${reason} Please correct it and resubmit from your profile.`
      : "Just a reminder. Please double-check your credential number and expiration date, then resubmit from your profile.",
    read: false,
  }, { skipDedupe: true }).catch(err => {
    console.warn("[sendVerificationReminder] notification insert blocked by RLS:", err?.message);
  });

  // Email the therapist too. Reuse the verification-result mailer, matching the
  // outcome to their current status. A notice that did not go is returned as text, never thrown.
  const outcome = status === "rejected" ? "rejected" : "under_review";
  const noticeProblem = await invokeNotice("notify-verification-result", { user_id: userId, outcome, reason });

  return { ok: true, status, noticeProblem };
}

// Auto-expire a verified credential when its expiration date has passed
// role: "rbt" | "bcba" — BCBA message notes the 2-year cycle and CEU requirement
export async function autoExpireVerification(userId, role) {
  await supabase
    .from("therapist_profiles")
    .update({ verification_status: "expired" })
    .eq("user_id", userId);

  const message = role === "bcba"
    ? "Your BCBA credential has expired. Complete your 32 CEUs and renew with BACB, then resubmit for re-verification to restore your trust badge."
    : role === "bcaba"
    ? "Your BCaBA credential has expired. Complete your required CEUs and renew with BACB, then resubmit for re-verification to restore your trust badge."
    : "Your RBT credential has expired. Please renew your certification with BACB and resubmit for re-verification.";

  await createNotification({
    user_id: userId,
    type: "verification_expired",
    title: "Credential expired",
    body: message,
    read: false,
  }).then(null, () => {});
}

// Admin revokes a verified credential (BACB-initiated disciplinary action or confirmed lapse)
// Distinct from expiry — revocation means the credential is no longer valid and cannot be resubmitted
// without a new BACB certificate number.
export async function revokeVerification(userId) {
  await supabase.from("therapist_profiles")
    .update({ verification_status: "revoked" })
    .eq("user_id", userId);
  await supabase.from("verification_requests")
    .update({ status: "revoked", reviewed_at: new Date().toISOString() })
    .eq("user_id", userId)
    .then(() => {}).catch(() => {});

  await createNotification({
    user_id: userId,
    type: "verification_revoked",
    title: "Credential verification revoked",
    body: "Your credential verification has been revoked. This typically indicates a change in standing with BACB. Please contact support if you believe this is an error.",
    read: false,
  }).then(null, () => {});
}

// Admin re-opens a rejected/revoked credential for review (sets status back to pending)
export async function reReviewVerification(userId) {
  const { error } = await supabase.from("therapist_profiles")
    .update({ verification_status: "pending", verification_rejection_reason: null })
    .eq("user_id", userId);
  if (error) throw error;
  await createNotification({
    user_id: userId,
    type: "verification_pending",
    title: "Credential under re-review",
    body: "An admin has opened your credential for re-review. No action is needed from you at this time.",
    read: false,
  }).then(null, () => {});
}

// Admin manually verifies a worker's credential without requiring the user to submit first.
// Used when the admin has independently confirmed credentials via BACB and wants to bypass
// the self-service submission flow.
export async function adminManualVerify(userId, { credential, expirationDate, note, role } = {}) {
  const update = {
    verification_status: "verified",
    ...(credential      ? { credential }                       : {}),
    ...(expirationDate  ? { expiration_date: expirationDate }  : {}),
    ...(note            ? { verification_notes: note }         : {}),
  };
  const { error } = await supabase.from("therapist_profiles")
    .upsert({ user_id: userId, ...update }, { onConflict: "user_id", ignoreDuplicates: false });
  if (error) throw error;

  // Log to verification_requests as an admin-initiated entry
  await supabase.from("verification_requests").insert({
    user_id: userId,
    credential_number: credential || null,
    status: "approved",
    submitted_at: new Date().toISOString(),
    reviewed_at: new Date().toISOString(),
  }).then(() => {}).catch(() => {});

  const cycleNote = (role === "bcba" || role === "bcaba")
    ? " Your certification renews every 2 years. You'll be reminded 60 days before expiry."
    : " Your certification renews annually. You'll be reminded 30 days before expiry.";
  const credLabel = typeLabel(roleTypeKey(role) || "RBT");
  await createNotification({
    user_id: userId,
    type: "verification_approved",
    title: "Credential verified",
    body: `Your BACB ${credLabel} credential has been verified by an admin. A trust badge is now shown on your profile and all applications.${cycleNote}`,
    read: false,
  }, { skipDedupe: true }).catch(() => {});
}

// Admin clears a therapist's background check
export async function clearBgc(userId) {
  const { error } = await supabase.from("therapist_profiles")
    .update({ bg_check: "Cleared" })
    .eq("user_id", userId);
  if (error) throw error;
  await createNotification({
    user_id: userId,
    type: "bgc_cleared",
    title: "Background check cleared",
    body: "Your background check has been reviewed and cleared. This status is now visible to agencies on your applications.",
    read: false,
  }).then(null, () => {});
}

// Send a "credential expiring soon" notification — deduped within the lookback window
// Warn window: 30 days for RBT (annual), 60 days for BCBA (2-year cycle, CEUs take time)
export async function notifyExpiringSoon(userId, role) {
  if (!userId) return;
  try {
    const warnDays   = role === "bcba" ? getCachedConfig("bcba_warn_days") : role === "bcaba" ? getCachedConfig("bcaba_warn_days") : getCachedConfig("rbt_warn_days");
    const dedupeDays = warnDays + (role === "rbt" ? 5 : 10); // slightly wider than warn window to avoid double-fire
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dedupeDays);
    const { data: existing } = await supabase
      .from("notifications")
      .select("id")
      .eq("user_id", userId)
      .eq("type", "verification_expiring_soon")
      .gte("created_at", cutoff.toISOString())
      .maybeSingle();
    if (existing) return;

    const message = role === "bcba"
      ? `Your BCBA credential expires in ${warnDays} days. Ensure your 32 CEUs are complete, then renew with BACB and resubmit for verification before it lapses.`
      : role === "bcaba"
      ? `Your BCaBA credential expires in ${warnDays} days. Ensure your required CEUs are complete, then renew with BACB and resubmit for verification before it lapses.`
      : `Your RBT credential expires in ${warnDays} days. Renew with BACB and resubmit for verification before it lapses to keep your trust badge active.`;

    await createNotification({
      user_id: userId,
      type: "verification_expiring_soon",
      title: "Credential expiring soon",
      body: message,
      read: false,
    });
  } catch (_) {}
}

// ── Browse therapists (agency view) ─────────────────────────────────────────
// Returns all therapist profiles formatted for TherapistSearch component.

function relativeTime(isoStr) {
  if (!isoStr) return "Unknown";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hrs   = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 60)  return "Today";
  if (hrs  < 24)  return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)   return `${days} days ago`;
  return `${Math.floor(days / 7)} wk ago`;
}

function initials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Card accent for a therapist row: the provider-role color from theme.js (same as the role chips and map pins).
// Rows that are not a provider role keep the neutral navy.
function roleAccent(t) {
  const key = roleTypeKey(t.role, t.credential_kind || t.credential);
  return key ? TYPE_COLOR[key].accent : "#1E1B4B";
}

/**
 * @description Maps a therapist row (from agency_find_providers_in_radius or
 *   agency_find_remote_bcbas) to the shared UI card shape. Extracted so radius and
 *   nationwide-remote loaders return identical objects. Remote fields
 *   (openToRemote/openToLicensure/licensedStates) default off when the source
 *   RPC doesn't project them (e.g. the local radius scan).
 * @param {Object} t - Raw therapist row from a discovery RPC
 * @returns {Object} UI-shaped therapist card
 */
function mapTherapistCard(t) {
  const type = (t.role || "rbt").toUpperCase();
  const name = t.full_name || "Anonymous";
  return {
    id:                 t.user_id,
    name,
    initials:           initials(name),
    avatarUrl:          t.avatar_url || null,
    accentColor:        roleAccent(t),
    type,
    credential:         t.credential          || "",
    verificationStatus: t.verification_status || "unverified",
    credentialedYear:   t.credentialed_year   ?? null,
    // Month-precision credential date ("YYYY-MM") — drives friendly month labels
    // when the RPC projects credentialed_date; otherwise degrades to year/legacy.
    credentialedDate:   t.credentialed_date ? String(t.credentialed_date).slice(0, 7) : null,
    // Derived (number) — fresh when the RPC projects credentialed_year; falls
    // back to the legacy snapshot column otherwise.
    experience:         t.credentialed_year ? yearsFromCredentialYear(t.credentialed_year) : (t.experience || 0),
    state:              t.state               || "",
    city:               t.city                || "",
    zip:                t.zip                 || "",
    lat:                t.lat                 ?? null,
    lng:                t.lng                 ?? null,
    coords:             (t.lat && t.lng) ? [t.lat, t.lng] : null,
    distanceMiles:      t.distance_miles      ?? null,
    maxDistance:        String(t.max_distance ?? 20),
    openToRemote:       t.open_to_remote      || false,
    openToLicensure:    t.open_to_licensure   || false,
    licensedStates:     t.licensed_states     || [],
    employment:         t.employment          || [],
    skills:             t.skills              || [],
    settings:           t.settings            || [],
    ageGroups:          t.age_groups          || [],
    availability:       t.availability        || [],
    hoursWanted:        t.min_hours && t.max_hours
                          ? `${t.min_hours}–${t.max_hours}`
                          : t.max_hours ? String(t.max_hours) : "—",
    about:              t.bio                 || "",
    certifications:     t.certifications      || [],
    languages:          t.languages           || [],
    payerEnrollments:   t.payer_enrollments   || [],
    bgCheck:            (t.settings_json || {}).backgroundCheck || "",
    workExperience:     (t.settings_json || {}).workExperience  || "",
    availabilityPeriods: (t.settings_json || {}).availabilityPeriods || [],
    // Server identity rule (agency_find_providers_in_radius): until the provider engages, name is "First L.",
    // credential is the type and there is no photo. Older servers send neither field: treat as not shared.
    credentialKind:     t.credential_kind     || null,
    identityShared:     t.identity_shared === true,
    lastActive:         relativeTime(t.updated_at),
    openToWork:         true,
    assessments:        [],
  };
}

/**
 * @description Loads therapists within a given radius of a map center.
 *   Uses the agency_find_providers_in_radius DB function so haversine filtering
 *   happens in Postgres — only nearby therapists are transferred.
 *   Returns the same UI shape as loadTherapists(), with coords pre-set
 *   from stored lat/lng (no client-side geocoding needed). Pins are rounded to
 *   2 decimals (~0.7 mi) by the server and `distance_miles` is computed there
 *   from the exact location; callers should prefer it over client haversine.
 *   The server caps the radius at 200 mi and the result at 500 rows.
 * @param {number} lat - Center latitude
 * @param {number} lng - Center longitude
 * @param {number} [radiusMiles=25] - Search radius in miles (server max 200)
 * @param {number} [limit=200] - Max results (server max 500)
 * @returns {Promise<Object[]>} Array of UI-shaped therapist objects with distance_miles
 */
export async function loadTherapistsInRadius(lat, lng, radiusMiles = 25, limit = 200) {
  const { data, error } = await supabase.rpc("agency_find_providers_in_radius", {
    p_lat:          lat,
    p_lng:          lng,
    p_radius_miles: radiusMiles,
    p_limit:        limit,
  });
  if (error) throw error;
  return (data || []).map(mapTherapistCard);
}

/**
 * @description Loads remote-electing BCBAs nationwide for an agency's remote
 *   case, via the agency_find_remote_bcbas DB function. Returns the shared therapist
 *   card shape plus a `licensedInState` flag (true when the case's state is in
 *   the therapist's self-attested licensed_states). Soft-sort, not a filter —
 *   licensed-in-state candidates rank first, but open-to-licensure candidates
 *   stay visible (agencies sponsor licensure, e.g. the real cross-state case).
 * @param {string} caseId - The remote case to source candidates for
 * @param {number} [limit=200] - Max results
 * @returns {Promise<Object[]>} Therapist cards, licensed-in-state first
 */
export async function loadRemoteBcbasForCase(caseId, limit = 200) {
  const { data, error } = await supabase.rpc("agency_find_remote_bcbas", {
    p_case_id: caseId,
    p_limit:   limit,
  });
  if (error) throw error;
  return (data || []).map(t => ({
    ...mapTherapistCard(t),
    // The RPC computes this against the case's state; default false if absent.
    licensedInState: t.licensed_in_state || false,
  }));
}

/**
 * @description Loads a small, non-identifying sample of active providers for the
 *   public (guest) home page's "providers" carousel, via the
 *   public_provider_highlights SECURITY DEFINER RPC. Anon has no RLS read path
 *   to profiles/therapist_profiles, so this deliberately minimal projection
 *   (initial, city, state, role) is the only guest-safe way to show "real
 *   people are here" without leaking identity. Never throws — this is a public
 *   marketing page and must degrade silently on any error.
 * @param {number} [limit=12] - Max providers to return
 * @returns {Promise<Object[]>} Array of `{ initial, city, state, role }`, or [] on error
 */
export async function loadPublicProviderHighlights(limit = 12) {
  try {
    const { data, error } = await supabase.rpc("public_provider_highlights", { p_limit: limit });
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

/**
 * @description Loads the anonymous provider pool that reach math runs over
 *   (marketReach: Market Reach widget + per-case reach stats; CasePosting's
 *   "providers within 25 miles" count). Served by the agency_provider_supply
 *   SECURITY DEFINER RPC, which returns only what that math reads: role,
 *   credential, verification, skills/settings/availability, travel range and
 *   coordinates rounded to ~0.7 mi. No id, name, avatar, bio or contact data
 *   ever reaches the client, and the caller must be an agency or admin.
 *
 *   The returned objects keep the same keys the old direct-table loader
 *   produced so marketReach and CasePosting are untouched; identity fields are
 *   neutral (`id` is a pool-local index, `name` is generic) and fields the
 *   RPC does not project default to empty. `zip` is only populated for a row
 *   with no coordinates, which marketReach geocodes to place it.
 * @returns {Promise<Object[]>} Array of UI-shaped provider pool rows
 */
export async function loadTherapists() {
  const { data, error } = await supabase.rpc("agency_provider_supply", { p_limit: 1000 });
  if (error) throw error;
  return (data || []).map((t, i) => {
    const type = (t.role || "rbt").toUpperCase();
    return {
      id:           `pool-${i}`,
      name:         "Provider",
      initials:     "??",
      accentColor:  roleAccent(t),
      type,
      credential:         t.credential         || "",
      verificationStatus: t.verification_status || "unverified",
      credentialedYear: null,
      credentialedDate: null,
      experience:   0,
      state:        "",
      city:         "",
      zip:          t.zip         || "",
      lat:          t.lat         ?? null,
      lng:          t.lng         ?? null,
      maxDistance:  String(t.max_distance ?? 20),
      employment:   [],
      skills:       t.skills      || [],
      settings:     t.settings    || [],
      ageGroups:    [],
      availability: t.availability || [],
      hoursWanted:  "—",
      about:        "",
      certifications:   [],
      languages:        [],
      payerEnrollments: [],
      bgCheck:          "",
      workExperience:   "",
      lastActive:   "Unknown",
      openToWork:   true,
      assessments:  [],
    };
  });
}

// ── Mark user onboarded ──────────────────────────────────────────────────────

/**
 * @description Marks a user's profile as onboarded in the profiles table.
 * @param {string} userId - The user's Supabase user ID
 * @returns {Promise<void>}
 */
export async function markUserOnboarded(userId) {
  const { error } = await supabase.from("profiles").update({ onboarded: true }).eq("id", userId);
  if (error) throw error;
}

// ── Agency profile ───────────────────────────────────────────────────────────

/**
 * @description Loads an agency's full profile for the AgencyProfileModal.
 *   Joins agency_profiles + profiles, merges into the modal's expected shape,
 *   and selects extended admin-only fields when `adminView` is true.
 *
 *   Lookup priority: `agencyId` if provided, otherwise `agencyName`.
 *
 * @param {Object} args
 * @param {string} [args.agencyId]
 * @param {string} [args.agencyName]
 * @param {boolean} [args.adminView=false]
 * @returns {Promise<Object|null>} Merged modal-shape row or null
 */
export async function loadAgencyProfileForModal({ agencyId, agencyName, adminView = false } = {}) {
  if (!agencyId && !agencyName) return null;

  const profileFields = adminView
    ? "id, full_name, phone, email, avatar_url, role, plan, email_verified, email_verified_at, deleted_at, deletion_requested_at, created_at"
    : "id, full_name, phone, email, avatar_url, role";

  // Admins read the full row; everyone else reads the public card (contact only where the agency made it public).
  let agencyQuery  = supabase.from(adminView ? "agency_profiles" : "agency_public_profiles").select("*");
  let profileQuery = supabase.from("profiles").select(profileFields);

  if (agencyId) {
    agencyQuery  = agencyQuery.eq("user_id", agencyId);
    profileQuery = profileQuery.eq("id", agencyId);
  } else {
    agencyQuery  = agencyQuery.eq("agency_name", agencyName).limit(1);
    profileQuery = profileQuery.eq("full_name", agencyName).eq("role", "agency").limit(1);
  }

  const [{ data: agencyData }, { data: profileData }] = await Promise.all([
    agencyQuery.maybeSingle(),
    profileQuery.maybeSingle(),
  ]);

  if (!agencyData && !profileData) return null;

  // Admin compliance view: attach the latest HIPAA BAA acceptance (version,
  // when, and IP) so moderators can audit it alongside the profile.
  let _baa = null;
  if (adminView) {
    const baaAgencyId = agencyId || agencyData?.user_id || profileData?.id;
    if (baaAgencyId) {
      const { data: baaRows } = await supabase
        .from("baa_acceptances")
        .select("version, accepted_at, ip_address")
        .eq("agency_id", baaAgencyId)
        .order("accepted_at", { ascending: false })
        .limit(1);
      _baa = baaRows?.[0] || null;
    }
  }

  if (agencyData) {
    return {
      ...agencyData,
      agency_name: agencyData.agency_name || profileData?.full_name || agencyName,
      dba_name:    agencyData.dba_name    || agencyData.agency_name || profileData?.full_name || agencyName,
      email:       agencyData.email        || profileData?.email     || null,
      phone:       agencyData.phone        || profileData?.phone     || null,
      logo_url:    agencyData.logo_url     || profileData?.avatar_url || null,
      _baa,
      _plan:                  profileData?.plan              ?? null,
      _email_verified:        profileData?.email_verified    ?? false,
      _email_verified_at:     profileData?.email_verified_at ?? null,
      _deleted_at:            profileData?.deleted_at        ?? null,
      _deletion_requested_at: profileData?.deletion_requested_at ?? null,
      _created_at:            profileData?.created_at        ?? null,
    };
  }

  // No agency_profiles row but the user exists — return a hydrated default
  return {
    user_id:      profileData.id,
    agency_name:  profileData.full_name || agencyName,
    dba_name:     profileData.full_name || agencyName,
    email:        profileData.email     || null,
    phone:        profileData.phone     || null,
    logo_url:     profileData.avatar_url || null,
    about: null, city: null, state: null, zip: null, address: null,
    accreditations: [], specialties: [], languages: [], diagnoses: [],
    service_settings: [], website: null, tagline: null,
    founded: null, staff_count: null, clients_served: null, service_areas: null,
    npi: null, npi_verified: false, npi_org_name: null, npi_verified_at: null,
    profile_complete: false, flagged_for_purge: false, fraud_flagged: false,
    fraud_signals: [], trial_eligible: true,
    registered_at: null, onboarded_at: null, updated_at: null,
    incomplete_reminder_sent_at: null, flagged_for_purge_at: null,
    _baa,
    _plan:                  profileData?.plan              ?? null,
    _email_verified:        profileData?.email_verified    ?? false,
    _email_verified_at:     profileData?.email_verified_at ?? null,
    _deleted_at:            profileData?.deleted_at        ?? null,
    _deletion_requested_at: profileData?.deletion_requested_at ?? null,
    _created_at:            profileData?.created_at        ?? null,
  };
}

/**
 * @description Loads a therapist's full profile for the WorkerProfileModal.
 *   Joins therapist_profiles + profiles, merges into the modal's expected
 *   shape, and selects extended admin-only fields when `adminView` is true.
 *
 * @param {Object} args
 * @param {string} args.userId
 * @param {string} [args.workerName] - Fallback display name if profile row missing
 * @param {boolean} [args.adminView=false]
 * @returns {Promise<Object|null>} Merged modal-shape row or null
 */
export async function loadTherapistProfileForModal({ userId, workerName, adminView = false } = {}) {
  if (!userId) return null;

  const profileFields = adminView
    ? "id, full_name, role, created_at, email, email_verified, email_verified_at, suspended, deleted_at, deletion_requested_at"
    : "id, full_name, role, created_at";

  const [{ data: p }, { data: tp }] = await Promise.all([
    supabase.from("profiles").select(profileFields).eq("id", userId).maybeSingle(),
    supabase.from("therapist_profiles").select("*").eq("user_id", userId).maybeSingle(),
  ]);

  if (!p && !tp) return null;

  return {
    ...(tp || {}),
    user_id:    userId,
    full_name:  p?.full_name  || workerName || "—",
    role:       p?.role       || "rbt",
    created_at: p?.created_at || null,
    // camelCase experience inputs so formatExperience() can build a friendly label.
    credentialedYear: tp?.credentialed_year ?? null,
    credentialedDate: tp?.credentialed_date ? String(tp.credentialed_date).slice(0, 7) : null,
    _email:                 p?.email                 ?? null,
    _email_verified:        p?.email_verified        ?? false,
    _email_verified_at:     p?.email_verified_at     ?? null,
    _suspended:             p?.suspended             ?? false,
    _deleted_at:            p?.deleted_at            ?? null,
    _deletion_requested_at: p?.deletion_requested_at ?? null,
    _created_at:            p?.created_at            ?? null,
    _suspended_at:          null,
  };
}

// Short-TTL cache + in-flight dedup for agency profile reads. App.js (logo/
// name on login), AgencyAbout (the editor), and the dashboard snapshot all
// call this independently — without dedup they each fire their own
// agency_profiles query within the same second on every mount/remount.
const _agencyProfileCache = new Map();   // userId → { data, at }
const _agencyProfileInflight = new Map(); // userId → Promise
const AGENCY_PROFILE_TTL_MS = 30_000;

export function invalidateAgencyProfile(userId) {
  _agencyProfileCache.delete(userId);
  _agencyProfileInflight.delete(userId);
}

/**
 * @description Loads an agency's profile from agency_profiles. Cached for 30s
 *   with in-flight dedup so concurrent callers share one network round-trip.
 * @param {string} userId - The agency's Supabase user ID
 * @param {{forceRefresh?: boolean}} [opts]
 * @returns {Promise<{profile: Object, publicFields: Object}|null>} Agency data or null
 */
export async function loadAgencyProfile(userId, opts = {}) {
  if (!userId) return null;
  const { forceRefresh = false } = opts;

  if (!forceRefresh) {
    const cached = _agencyProfileCache.get(userId);
    if (cached && Date.now() - cached.at < AGENCY_PROFILE_TTL_MS) return cached.data;
    const inflight = _agencyProfileInflight.get(userId);
    if (inflight) return inflight;
  }

  const promise = (async () => {
    try {
      const { data, error } = await supabase
        .from("agency_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      const shaped = data ? dbToAgency(data) : null;
      _agencyProfileCache.set(userId, { data: shaped, at: Date.now() });
      return shaped;
    } finally {
      _agencyProfileInflight.delete(userId);
    }
  })();
  _agencyProfileInflight.set(userId, promise);
  return promise;
}

/**
 * @description Saves (upserts) an agency profile to agency_profiles. Enforces NPI
 *   uniqueness and triggers async duplicate signal detection.
 * @param {string} userId - The agency's Supabase user ID
 * @param {Object} profile - The UI-shaped agency profile object
 * @param {Object} publicFields - Visibility flags {phone, email, website}
 * @returns {Promise<void>}
 */
export async function saveAgencyProfile(userId, profile, publicFields) {
  // NPI uniqueness guard — prevent saving a duplicate NPI
  if (profile.npi) {
    const { data: existing } = await supabase
      .from("agency_profiles")
      .select("user_id")
      .eq("npi", profile.npi)
      .neq("user_id", userId)
      .limit(1);
    if (existing?.length > 0) {
      throw new Error("This NPI is already registered to another account");
    }
  }

  const row = agencyToDb(userId, profile, publicFields);
  const { error } = await supabase
    .from("agency_profiles")
    .upsert(row, { onConflict: "user_id" });
  if (error) throw error;

  // Sync the agency name into profiles.full_name. The app derives the agency's
  // display name (used in the dashboard, messages, and the denormalized
  // cases.agency_name) from profiles.full_name — but agencies enter their name
  // in agency_profiles.agency_name. Without this sync, full_name stays empty and
  // the agency shows up nameless (and posts cases with a null agency_name).
  if (profile.name) {
    await supabase.from("profiles").update({ full_name: profile.name }).eq("id", userId)
      .then(() => {}).catch(() => {});
  }

  // Bust the read cache so the next loadAgencyProfile() returns fresh data.
  invalidateAgencyProfile(userId);

  // Async fraud signal check — runs after save, never blocks
  if (profile.phone || profile.npi) {
    checkDuplicateSignals({
      agencyId:   userId,
      agencyName: profile.name || "",
      zip:        profile.zip  || "",
      phone:      profile.phone || null,
      ip:         null,
    }).catch(() => {});
  }
}

function dbToAgency(row) {
  return {
    profile: {
      name:          row.agency_name    || "",
      dbaName:       row.dba_name       || row.agency_name || "",
      tagline:       row.tagline        || "",
      about:         row.about          || "",
      founded:       row.founded        || "",
      staffCount:    row.staff_count    || "",
      clientsServed: row.clients_served || "",
      city:          row.city           || "",
      state:         row.state          || "",
      zip:           row.zip            || "",
      serviceAreas:  row.service_areas  || "",
      phone:         row.phone          || "",
      email:         row.email          || "",
      website:       row.website        || "",
      npi:            row.npi             || "",
      npiVerified:    row.npi_verified   || false,
      npiOrgName:     row.npi_org_name   || "",
      npiVerifiedAt:  row.npi_verified_at || null,
      isFounder:      row.is_founder      || false,
      founderExpiresAt: row.founder_expires_at || null,
      accreditations: normalizeAccreditations(row.accreditations),
      languages:     row.languages      || [],
      diagnoses:     row.diagnoses      || [],
      settings:      row.service_settings || [],
      logoUrl:       row.logo_url       || "",
    },
    // Privacy-by-default: legacy rows with no public_fields keep phone/email
    // private (inbox is the channel); website stays public (marketing).
    publicFields: row.public_fields || { phone: false, email: false, website: true },
  };
}

function agencyToDb(userId, p, publicFields) {
  return {
    user_id:         userId,
    agency_name:     p.name           || null,
    // Display Name (DBA). Fall back to the legal name so it is never nulled/blank
    // on a partial save; a DB trigger propagates edits to the agency's cases.
    dba_name:        p.dbaName        || p.name || null,
    tagline:         p.tagline        || null,
    about:           p.about          || null,
    founded:         p.founded        || null,
    staff_count:     p.staffCount     || null,
    clients_served:  p.clientsServed  || null,
    city:            p.city           || null,
    state:           p.state          || null,
    zip:             p.zip            || null,
    service_areas:   p.serviceAreas   || null,
    phone:           p.phone          || null,
    email:           p.email          || null,
    website:         p.website        || null,
    npi:             p.npi            || null,
    npi_verified:    p.npiVerified    || false,
    npi_org_name:    p.npiOrgName     || null,
    npi_verified_at: p.npiVerified ? new Date().toISOString() : null,
    accreditations:  p.accreditations || [],
    languages:       p.languages      || [],
    diagnoses:       p.diagnoses      || [],
    service_settings: p.settings      || [],
    public_fields:   publicFields,
    logo_url:        p.logoUrl        || null,
  };
}

/**
 * @description Upload a cropped profile image (a data-URL from the canvas
 *   cropper) to the public `profile-images` Storage bucket and return its
 *   public URL. This replaces the old "base64 string in a text column" model:
 *   the DB row stays tiny and the image is served from the CDN instead of
 *   through PostgREST egress (a single uncropped 2.8 MB photo pulled across the
 *   applicant list / inbox was the dominant egress driver).
 * @param {string} userId   Owner id — also the storage folder, so the bucket's
 *   owner-scoped RLS ({uid}/...) lets a user only write/replace their own image.
 * @param {string} dataUrl  "data:image/...;base64,..." from getCroppedImg().
 *   If it's already a plain URL (or empty), it's returned unchanged (no-op).
 * @param {"avatar"|"logo"} kind  Filename prefix.
 * @returns {Promise<string>} Public, cache-busted URL to store on the profile row.
 */
// Convert a base64 data URL to a Blob WITHOUT fetch(). Fetching a `data:` URL
// is governed by the CSP `connect-src` directive, and our policy doesn't (and
// shouldn't need to) allow `data:` there — so `fetch(dataUrl)` throws
// "Load failed" and the upload silently dies. Decoding the base64 directly
// avoids the network layer entirely.
function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(",");
  const mime = (header.match(/data:([^;]+)/)?.[1]) || "image/jpeg";
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export async function uploadProfileImage(userId, dataUrl, kind = "avatar") {
  if (!userId) throw new Error("uploadProfileImage: missing userId");
  if (!dataUrl || !dataUrl.startsWith("data:")) return dataUrl || ""; // already a URL / empty

  const blob = dataUrlToBlob(dataUrl); // data-URL → Blob (no fetch; CSP-safe)
  const path = `${userId}/${kind}.jpg`;             // stable, owner-scoped (RLS by 1st segment)
  const { error } = await supabase.storage
    .from("profile-images")
    .upload(path, blob, { upsert: true, contentType: blob.type || "image/jpeg", cacheControl: "3600" });
  if (error) throw error;

  const { data } = supabase.storage.from("profile-images").getPublicUrl(path);
  return `${data.publicUrl}?v=${Date.now()}`; // cache-bust so a replaced image shows immediately
}

// ── Résumé upload (private bucket, PDF only, ≤ 2.5 MB) ─────────────────────
export const RESUME_MAX_BYTES = 2_621_440;        // 2.5 MB
export const RESUME_ACCEPT     = "application/pdf";

/** Validate a candidate résumé File. Returns an error KEY (i18n) or null if OK. */
export function validateResumeFile(file) {
  if (!file) return "therapist.resumeErrorMissing";
  if (file.type !== RESUME_ACCEPT) return "therapist.resumeErrorType";
  if (file.size > RESUME_MAX_BYTES) return "therapist.resumeErrorSize";
  return null;
}

/**
 * Upload a therapist's résumé PDF to the private `resumes` bucket and record its
 * path on their profile. Overwrites any existing résumé (stable owner-scoped
 * path). Returns the storage path. Throws on validation/upload/db error.
 * @param {string} userId
 * @param {File}   file  a PDF File from an <input type="file">
 */
export async function uploadResume(userId, file) {
  if (!userId) throw new Error("uploadResume: missing userId");
  const invalid = validateResumeFile(file);
  if (invalid) { const e = new Error(invalid); e.code = invalid; throw e; }

  const path = `${userId}/resume.pdf`;              // stable, owner-scoped (RLS by 1st segment)
  const { error: upErr } = await supabase.storage
    .from("resumes")
    .upload(path, file, { upsert: true, contentType: "application/pdf", cacheControl: "3600" });
  if (upErr) throw upErr;

  const { error: dbErr } = await supabase
    .from("therapist_profiles").update({ resume_path: path }).eq("user_id", userId);
  if (dbErr) throw dbErr;
  return path;
}

/**
 * Does this provider have a résumé an agency can open? An uploaded PDF (resume_path) or a legacy http(s) link.
 * Mirrors the server rule in request_resume / on_therapist_resume_added (migration 20261119090000).
 * @param {{ resumePath?: string, resumeUrl?: string, resume_path?: string, resume_url?: string }|null} p
 */
export function hasResumeOnFile(p) {
  if (!p) return false;
  const path = p.resumePath ?? p.resume_path ?? "";
  const url  = p.resumeUrl ?? p.resume_url ?? "";
  return !!String(path).trim() || /^https?:\/\//i.test(String(url));
}

/** The signed-in provider's own résumé state (for the inbox request card). */
export async function getOwnResumeStatus(userId) {
  if (!userId) return { hasResume: false };
  const { data } = await supabase.from("therapist_profiles")
    .select("resume_path, resume_url").eq("user_id", userId).maybeSingle();
  return { hasResume: hasResumeOnFile(data) };
}

/**
 * Where a provider's résumé lives: an uploaded PDF path (open it in the in-app viewer via getResumeSignedUrl) or a
 * legacy external link. RLS decides who may read the profile (the provider, or an agency they applied to).
 * @returns {Promise<{ path: string, legacyUrl: string }>}
 */
export async function getResumeSource(userId) {
  if (!userId) return { path: "", legacyUrl: "" };
  const { data } = await supabase.from("therapist_profiles")
    .select("resume_path, resume_url").eq("user_id", userId).maybeSingle();
  return {
    path: data?.resume_path || "",
    legacyUrl: /^https?:\/\//i.test(data?.resume_url || "") ? data.resume_url : "",
  };
}

/**
 * A link to open a provider's résumé: a short-lived signed URL for an uploaded PDF, else the legacy link.
 * RLS decides who may read the profile and the file (the provider, or an agency they applied to). "" if none.
 */
export async function getResumeLinkForUser(userId) {
  if (!userId) return "";
  const { data } = await supabase.from("therapist_profiles")
    .select("resume_path, resume_url").eq("user_id", userId).maybeSingle();
  if (data?.resume_path) return getResumeSignedUrl(data.resume_path);
  return /^https?:\/\//i.test(data?.resume_url || "") ? data.resume_url : "";
}

/** Remove a therapist's résumé (storage file + profile pointer). */
export async function removeResume(userId) {
  if (!userId) return;
  await supabase.storage.from("resumes").remove([`${userId}/resume.pdf`]).catch(() => {});
  await supabase.from("therapist_profiles").update({ resume_path: null }).eq("user_id", userId);
}

/**
 * Mint a short-lived signed URL for a résumé in the private bucket. RLS decides
 * whether the caller is allowed (the owner, or an agency the therapist applied
 * to) — a non-permitted caller gets an error and we return "". No permanent
 * public link is ever exposed.
 * @param {string} resumePath  the stored resume_path (e.g. "<uid>/resume.pdf")
 * @param {number} [expiresIn=3600]  seconds the link stays valid
 * @returns {Promise<string>} a signed URL, or "" if unavailable/forbidden
 */
export async function getResumeSignedUrl(resumePath, expiresIn = 3600) {
  if (!resumePath) return "";
  try {
    const { data, error } = await supabase.storage
      .from("resumes").createSignedUrl(resumePath, expiresIn);
    if (error) return "";
    return data?.signedUrl || "";
  } catch {
    return "";
  }
}
